import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "~/design-system/Box";
import { useBreakpoint } from "~/design-system/hooks";
import { Text } from "~/design-system/Text";

const OFFLINE_BANNER_HEIGHT = 45; // mobile

function useOfflineBannerMountState(isOffline: boolean, breakpoint: string) {
  const [isMounted, setIsMounted] = React.useState(false);
  const [shouldShow, setShouldShow] = React.useState(false);
  const prevOffline = React.useRef(isOffline);

  React.useEffect(() => {
    if (breakpoint === "mobile") {
      if (isOffline && !prevOffline.current) {
        setShouldShow(true);
        setIsMounted(true);
      } else if (!isOffline && prevOffline.current) {
        setShouldShow(false);
      }
    } else {
      setShouldShow(isOffline);
      setIsMounted(isOffline);
    }
    prevOffline.current = isOffline;
  }, [isOffline, breakpoint]);

  const handleExitComplete = React.useCallback(() => {
    setIsMounted(false);
  }, []);

  return { handleExitComplete, isMounted, shouldShow };
}

interface OfflineBannerProps {
  isOffline: boolean;
  visible: boolean;
  onExitComplete?: () => void;
}

const OfflineBannerMobile = ({
  isOffline,
  onExitComplete,
  visible,
}: OfflineBannerProps) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {visible && (
        <motion.div
          initial={{ opacity: 1, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 1, scaleY: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{
            backgroundColor: "var(--colors-caution300)",
            borderTop: "var(--colors-caution300) solid var(--space-xsmall)",
            display: "flex",
            justifyContent: "center",
            left: 0,
            minHeight: `${OFFLINE_BANNER_HEIGHT}px`,
            originY: 0,
            padding: "4px 0",
            position: "fixed",
            top: 0,
            width: "100vw",
            zIndex: 9999,
          }}
        >
          <Text
            size="small"
            tone="white"
            weight="strong"
            style={{
              marginBottom: 6,
              textAlign: "center",
              width: "100%",
            }}
          >
            {t(
              "screens.App.components.OfflineBanner.info",
              "You are currently offline."
            )}
          </Text>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const OfflineBannerDesktop = ({
  isOffline,
  onExitComplete,
  visible,
}: OfflineBannerProps) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0.8 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleY: 0.8 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ originY: 0 }}
        >
          <Box
            width="full"
            display="flex"
            justifyContent="center"
            css={{
              backgroundColor: "transparent",
              borderTop: "8px solid $colors$caution300",
            }}
          >
            <Box
              css={{
                alignItems: "center",
                backgroundColor: "$colors$caution300",
                borderRadius: "0 0 8px 8px",
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                display: "flex",
                justifyContent: "center",
                marginTop: "-17px",
                minWidth: "160px",
                padding: "6px 0",
                position: "relative",
                top: "12px",
              }}
            >
              <Text
                size="small"
                tone="white"
                weight="strong"
                style={{ textAlign: "center", width: "100%" }}
              >
                {t(
                  "screens.App.components.OfflineBanner.info",
                  "You are currently offline."
                )}
              </Text>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const OfflineBanner = (props: OfflineBannerProps) => {
  const { breakpoint } = useBreakpoint();
  return breakpoint === "mobile" ? (
    <OfflineBannerMobile {...props} />
  ) : (
    <OfflineBannerDesktop {...props} />
  );
};

export { OFFLINE_BANNER_HEIGHT, OfflineBanner, useOfflineBannerMountState };
